
import { Component, Vue, Prop } from 'vue-property-decorator'
import { Client, Workspace, ClientWorkspacesFilters, DomainCatalogGridItem, BLUEMESSAGING_ID, WorkspaceStatus } from '@/types/Catalogs'
import DateSelector from '@/components/generic/DateSelector.vue'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import catalogsService from '@/services/CatalogsService'
import { PermissionNames, RoleNames } from '@/types/Auth'
import userInfoStore from '@/store/userInfo'
import { PageRequest } from '@/types/Support'
import MessageService from '@/services/support/MessageService'
import LogService, { ToString } from '@/services/support/LogService'
import { MessageError } from '../generic/MessageHandling.vue'

@Component({
  components: {
    DateSelector,
    LoadingMask
  }
})
export default class AdminWorkspaces extends Vue {
  @Prop({ required: true, default: 0 })
  id: number

  @Prop({ required: true, default: new DomainCatalogGridItem() })
  domain: DomainCatalogGridItem

  @Prop({ required: true, default: '' })
  title: number

  @Prop({ required: true, default: [] })
  clients: any[]

  @Prop({ required: false, default: false })
  readOnly: number

  MAX_WORKSPACES_FOR_INDIVIDUAL_DOMAIN = 2
  PAGE_SIZE = 500
  formOpen = false
  clientSelectionOpen = false
  loading = false
  clientSelected: any = {}
  clientSelectedBackup: any = {}
  workspaces: Workspace[] = []
  workspaceSelected = new Workspace()
  workspacesStatus = [
    { value: WorkspaceStatus.ACTIVE, text: 'Activo', disabled: true },
    { value: WorkspaceStatus.INACTIVE, text: 'Baja', disabled: true },
    { value: WorkspaceStatus.SUSPENDED, text: 'Suspendido', disabled: true }
  ]
  searchClient = ''
  form = Workspace.getIntanceForEdit()

  regexPhone = /^[+]?[0-9]{10,15}$/
  displayChangeOwnerMessage: boolean = false
  confirmChangeOwner: boolean = false
  ownerChanged: boolean = false
  changingOwner: boolean = false

  mounted () {
    this.init()
  }

  get createPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.WORKSPACES_CREATE)
  }

  get updatePermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.WORKSPACES_UPDATE)
  }

  get userHasFinancesAdminRole () {
    return userInfoStore.hasRole(RoleNames.FINANCES_ADMIN)
  }

  /**
   * Validates if the current domain is INDIVIDUAL and
   * it has at least two clients where one of them is BLUEMESSAGING
   */
  get isIndividualDomainWithBLUE () {
    return this.domain.isIndividual() &&
      this.workspaces.length === this.MAX_WORKSPACES_FOR_INDIVIDUAL_DOMAIN &&
      this.isClientInWorkspaces(BLUEMESSAGING_ID)
  }

  /**
   * Validates if one client is already in the workspaces of the current domain
   *
   * @param clientId to check
   * @return true if the clientId is in some of the workspaces
   */
  isClientInWorkspaces (clientId: number): boolean {
    return this.workspaces.some((w: Workspace) => w.client.id === clientId)
  }

  init () {
    this.workspaces = []
    const pageRequest = new PageRequest<ClientWorkspacesFilters>(this.PAGE_SIZE)
    pageRequest.filters.domainId = this.id
    this.loadWorkspacesPage(pageRequest)
  }

  newItem () {
    this.workspaceSelected = Workspace.getIntanceForEdit()
    this.form = this.workspaceSelected
    this.searchClient = ''
    this.showForm(true)
    setTimeout(() => {
      document.getElementById('input-client')!.focus()
    }, 500)
  }

  updateItem () {
    this.form = JSON.parse(JSON.stringify(this.workspaceSelected))
    const client = this.clients.find((it: Client) => it.id === this.workspaceSelected.client.id)
    this.selectClient(client)
    this.showForm(true)
  }

  showForm (flag: boolean) {
    if (flag) {
      this.clientSelectedBackup = {}
      this.formOpen = true
    } else {
      this.formOpen = false
    }
  }

  showClientSelection (flag: boolean) {
    if (flag) {
      this.searchClient = ''
      this.clientSelected = {}
      this.clientSelectionOpen = true
    } else {
      setTimeout(() => {
        this.clientSelectionOpen = false
        if (this.clientSelectedBackup.code) {
          this.selectClient(this.clientSelectedBackup)
        }
      }, 200)
    }
  }

  loadWorkspacesPage (pageRequest: PageRequest<any>) {
    catalogsService.getWorkspacesByDomain(pageRequest).then(response => {
      this.workspaces.push(...response.results)

      if (this.workspaces.length < response.total) {
        pageRequest.page++
        this.loadWorkspacesPage(pageRequest)
      } else {
        this.$emit('workspacesLoaded', this.workspaces)
      }
    }).catch((error) => {
      LogService.error(`Error en la carga de Workspaces: ${ToString(error.response ?? error)}`)
    })
  }

  isMatchClient (client: any) {
    if (client.status !== WorkspaceStatus.ACTIVE) return false
    if (this.workspaces.length) {
      // Hide all clients that had already been related to this domain
      let clientIds = this.workspaces.map((w) => w.client.id)
      if (clientIds.includes(client.id)) return false
    }

    // If is an individual domain with at least one workspace that isn't
    // BLUEMESSAGING, only can select BLUEMESSAGING client
    if (this.domain.isIndividual() &&
      this.workspaces.length &&
      !this.isClientInWorkspaces(BLUEMESSAGING_ID) &&
      client.id !== BLUEMESSAGING_ID) return false

    if (!this.searchClient) return true

    // Match the client name
    const regexSearch = new RegExp(this.searchClient, 'i')
    return regexSearch.test(client.code) || regexSearch.test(client.name)
  }

  selectClient (client: any) {
    this.clientSelected = client
    this.clientSelectedBackup = client
    this.searchClient = client.code + ' ' + client.name
  }

  getWorkspaceStatusDescription (status: string) {
    return Workspace.getStatusDescription(status)
  }

  validateState (ref: any) {
    const thiz = this as any
    if (
      thiz.veeFields[ref] &&
      (thiz.veeFields[ref].dirty || thiz.veeFields[ref].validated)
    ) {
      return !thiz.veeErrors.has(ref)
    }
    return null
  }

  onSubmit () {
    if (!this.clientSelected.code) {
      MessageService.error('Validación de formulario', 'Debe elegir un cliente', 3000)
      return
    }
    this.form.domain.id = this.id
    this.form.client.id = this.clientSelected.id

    this.$validator.validateAll().then(result => {
      if (result) {
        this.loading = true
        // If it's a new item and there are no workspaces yet, then the item is set as owner
        if (!this.form.id && !this.workspaces.length) {
          if (!this.userHasFinancesAdminRole && this.form.client.id === BLUEMESSAGING_ID) {
            this.$bvToast.toast(
              'Lo sentimos, no se puede asociar a Bluemessaging como owner del dominio. Por favor, seleccione otro cliente que será owner de este dominio o contacte al Gerente de Fiananzas.',
              {
                title: 'Asignación no autorizada.',
                variant: 'danger',
                autoHideDelay: 10000
              }
            )
            this.loading = false
            return
          }
          this.form.owner = true
        }

        catalogsService.saveWorkspace(this.form).then((response: Client) => {
          this.init()
          this.showForm(false)
          this.$bvToast.toast(
            'El Workspace se agregado correctamente.',
            {
              title: 'Workspace completado.',
              variant: 'success',
              autoHideDelay: 6000
            }
          )
        }).catch(() => {
          MessageService.error('Error al guardar', 'No fue posible asociar la razón social')
        }).finally(() => {
          this.loading = false
        })
      }
    })
  }

  async saveAsOwner () {
    this.changingOwner = true
    if (this.workspaceSelected.owner) return

    if (!this.userHasFinancesAdminRole && this.workspaceSelected.client.id === BLUEMESSAGING_ID) {
      this.changingOwner = false
      this.displayChangeOwnerModal()
      this.$bvToast.toast(
        'Lo sentimos, no puede asociar a Bluemessaging como owner del dominio. Por favor, seleccione otro cliente que será owner de este dominio o contacte al Gerente de Fiananzas.',
        {
          title: 'Asignación no autorizada.',
          variant: 'danger',
          autoHideDelay: 10000
        }
      )
      return
    }

    for (let it of this.workspaces) {
      if (it.owner) {
        await catalogsService.saveWorkspaceOwner(it.id, false)
        it.owner = false
      }
    }
    await catalogsService.saveWorkspaceOwner(this.workspaceSelected.id, true)
      .then(() => {
        this.displayChangeOwnerModal()
        this.ownerChanged = true
        this.workspaceSelected.owner = true
        this.$bvToast.toast(
          'Se ha cambiado el workspace owner correctamente.',
          {
            title: 'Asignación completada.',
            variant: 'success',
            autoHideDelay: 5000
          }
        )
      })
      .catch((error) => {
        let messageValues = {
          title: 'Error en el cambio de cliente.',
          message: '',
          delay: 6000,
          error: error
        }
        new MessageError({ propsData: messageValues }).displayMessage()
      })
      .finally(() => {
        this.changingOwner = false
        this.hideOwnerToast()
      })
  }

  onCancel () {
    this.$emit('close')
  }

  displayChangeOwnerModal () {
    this.displayChangeOwnerMessage = !this.displayChangeOwnerMessage
    if (!this.displayChangeOwnerMessage) this.confirmChangeOwner = false
  }

  hideOwnerToast () {
    setTimeout(() => {
      this.ownerChanged = false
    }, 4000)
  }
}
